import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../components/TopBar";
import { api, fmtUSD, todayISO } from "../lib/api";
import { toast } from "sonner";
import { Save, Beef, Tractor, Calendar } from "lucide-react";

/** Year-end Fair-Market-Value wizard. One screen, inline edit every row's
 *  current value + "as of" date, then one Save button. */
export default function FMVWizard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultDate, setDefaultDate] = useState(todayISO());
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({}); // keyed by `${kind}:${id}` → {current_value, current_value_as_of}

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get("/fmv/items").then((r) => r.data);
      setRows(data);
      setEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load FMV items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rowKey = (r) => `${r.kind}:${r.id}`;
  const current = (r) => {
    const e = edits[rowKey(r)];
    if (!e) return { current_value: r.current_value, current_value_as_of: r.current_value_as_of };
    return e;
  };

  const setField = (r, patch) => {
    const key = rowKey(r);
    const prev = edits[key] || { current_value: r.current_value, current_value_as_of: r.current_value_as_of };
    setEdits({ ...edits, [key]: { ...prev, ...patch } });
  };

  const applyDefaultDate = () => {
    const next = { ...edits };
    rows.forEach((r) => {
      const key = rowKey(r);
      const existing = next[key] || { current_value: r.current_value, current_value_as_of: r.current_value_as_of };
      next[key] = { ...existing, current_value_as_of: defaultDate };
    });
    setEdits(next);
    toast.success(`Stamped ${defaultDate} on every row`);
  };

  const dirty = useMemo(() => {
    return rows.filter((r) => {
      const e = edits[rowKey(r)];
      if (!e) return false;
      return (
        parseFloat(e.current_value || 0) !== parseFloat(r.current_value || 0) ||
        (e.current_value_as_of || "") !== (r.current_value_as_of || "")
      );
    });
  }, [edits, rows]);

  const saveAll = async () => {
    if (dirty.length === 0) {
      toast.info("No changes to save");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        items: dirty.map((r) => {
          const e = current(r);
          return {
            kind: r.kind,
            id: r.id,
            current_value: parseFloat(e.current_value) || 0,
            current_value_as_of: e.current_value_as_of || defaultDate,
          };
        }),
      };
      const res = await api.post("/fmv/bulk-update", payload);
      const { livestock, equipment } = res.data.updated;
      toast.success(`Saved · ${livestock} livestock, ${equipment} equipment`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.kind === filter);
  }, [rows, filter]);

  const totals = useMemo(() => {
    let fmv = 0;
    let cost = 0;
    filtered.forEach((r) => {
      const e = current(r);
      fmv += parseFloat(e.current_value) || 0;
      cost += parseFloat(r.purchase_cost) || 0;
    });
    return { fmv, cost };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, edits]);

  return (
    <div>
      <TopBar
        title="Bulk FMV Update"
        subtitle="One screen, every animal and piece of equipment. Punch in year-end Fair Market Values, then save in one shot."
        actions={
          <button
            data-testid="fmv-save-all-btn"
            onClick={saveAll}
            disabled={busy || dirty.length === 0}
            className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#15372C] inline-flex items-center gap-2 disabled:opacity-40 mt-6"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : `Save (${dirty.length})`}
          </button>
        }
      />

      <div className="bg-white border border-[#1F2A47] p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1">
          <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#66645E] mb-1">Default "As Of" Date</div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#66645E]" />
            <input
              type="date"
              value={defaultDate}
              onChange={(e) => setDefaultDate(e.target.value)}
              data-testid="fmv-default-date-input"
              className="bg-white border border-[#1F2A47] px-3 py-1.5 text-sm"
            />
            <button
              onClick={applyDefaultDate}
              data-testid="fmv-apply-date-btn"
              className="border border-[#1F2A47] px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold hover:bg-[#1F2A47] hover:text-white"
            >
              Stamp All
            </button>
          </div>
        </div>
        <div className="flex gap-1 bg-[#F5F4F0] p-1 border border-[#E5E2D6]">
          {["all", "livestock", "equipment"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`fmv-filter-${k}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold capitalize ${filter === k ? "bg-[#1F2A47] text-white" : "text-[#1F2A47] hover:bg-white"}`}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white border border-[#1F2A47] p-3">
          <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#66645E]">Cost Basis</div>
          <div className="font-display text-2xl font-black tabular">{fmtUSD(totals.cost)}</div>
        </div>
        <div className="bg-[#1A4135] text-white p-3">
          <div className="text-[9px] uppercase tracking-[0.2em] font-bold opacity-80">Fair Market Value</div>
          <div className="font-display text-2xl font-black tabular" data-testid="fmv-totals-fmv">{fmtUSD(totals.fmv)}</div>
        </div>
      </div>

      <div className="bg-white border border-[#1F2A47] overflow-x-auto" data-testid="fmv-table">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#66645E]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#66645E]">No {filter === "all" ? "" : filter} assets.</div>
        ) : (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Name</th>
                <th>Category</th>
                <th className="num">Cost Basis</th>
                <th className="num">Current FMV</th>
                <th>As Of</th>
                <th className="num">Change</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const e = current(r);
                const isDirty = !!edits[rowKey(r)];
                const delta = (parseFloat(e.current_value) || 0) - (parseFloat(r.current_value) || 0);
                return (
                  <tr
                    key={rowKey(r)}
                    data-testid={`fmv-row-${r.kind}-${r.id}`}
                    className={isDirty ? "bg-[#FFF6E5]" : ""}
                  >
                    <td>
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider">
                        {r.kind === "livestock" ? <Beef className="w-3 h-3" /> : <Tractor className="w-3 h-3" />}
                        {r.kind}
                      </span>
                    </td>
                    <td className="font-semibold">
                      {r.name}
                      {r.head_count > 1 && <span className="text-[10px] text-[#66645E] ml-1">×{r.head_count}</span>}
                    </td>
                    <td className="text-xs text-[#66645E] capitalize">
                      {r.category}
                      {r.subtype && <span className="block text-[10px]">{r.subtype}</span>}
                    </td>
                    <td className="num text-[#66645E]">{fmtUSD(r.purchase_cost)}</td>
                    <td className="num">
                      <input
                        type="number"
                        step="0.01"
                        value={e.current_value ?? ""}
                        onChange={(ev) => setField(r, { current_value: ev.target.value })}
                        data-testid={`fmv-value-input-${r.kind}-${r.id}`}
                        className="w-28 bg-white border border-[#1F2A47] px-2 py-1 text-sm text-right tabular"
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        value={e.current_value_as_of || ""}
                        onChange={(ev) => setField(r, { current_value_as_of: ev.target.value })}
                        data-testid={`fmv-date-input-${r.kind}-${r.id}`}
                        className="bg-white border border-[#1F2A47] px-2 py-1 text-sm"
                      />
                    </td>
                    <td className={`num tabular font-bold ${delta > 0 ? "text-[#1A4135]" : delta < 0 ? "text-[#BC4727]" : "text-[#66645E]"}`}>
                      {delta === 0 ? "—" : (delta > 0 ? "+" : "") + fmtUSD(delta)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {dirty.length > 0 && (
        <div className="mt-3 text-[11px] text-[#5C4A2B] bg-[#FBE9E2]/30 border border-[#BC4727] p-3" data-testid="fmv-unsaved-banner">
          ⚠️ You have {dirty.length} unsaved change{dirty.length === 1 ? "" : "s"}. Hit <b>Save ({dirty.length})</b> to commit.
        </div>
      )}
    </div>
  );
}
